// Animation constants
export const ANIMATION_DURATIONS = {
    INITIAL_ZOOM: 1,
    SUBSEQUENT_MOVEMENT: 0.3,
    ZOOM_OUT_POSITION: 0.57,
    ZOOM_OUT_FRUSTUM: 0.85,
    ZOOM_OUT_DELAY: 0.25
};

// State enum - aligned with NavigationController states
export const SheetState = {
    OVERVIEW: 'OVERVIEW',
    ZOOMED_IN: 'ZOOMED_IN', 
    DETAIL_VIEW: 'DETAIL_VIEW',
    ANIMATING: 'ANIMATING'
};

// Gesture constants  
export const DOUBLE_TAP_THRESHOLD = 300; // ms
export const SWIPE_VELOCITY_THRESHOLD = 0.3;
export const SWIPE_DISTANCE_THRESHOLD = 50; // pixels
export const DRAG_THRESHOLD = 5; // pixels - distance before a click becomes a drag
export const CLICK_DISTANCE_THRESHOLD = 0.3; // world units - max distance from contact sheet to trigger image transition

// Frustum size limit for larger desktop displays
// Sheet height is ~3.76 units, so MIN_FRUSTUM = 5 gives 75% viewport fill (3.76/5 = 0.752)
// Ease into this between tablet and desktop widths so overview framing does not jump at a breakpoint.
export const MIN_FRUSTUM_SIZE = 5;
export const OVERVIEW_FRUSTUM_TRANSITION_START = 900; // px - keep smaller/mobile layouts unchanged below this
export const LARGE_DISPLAY_THRESHOLD = 1200; // px - start preserving overview whitespace on desktop widths

import { dataLoader } from '../utils/DataLoader.js';

// Legacy sheet configs - now moved to JSON files
// This object maintains backward compatibility while we transition
export const SHEET_CONFIGS = {};

// Sheet data loading functions
export async function getSheetData(sheetId) {
    const data = await dataLoader.loadSheetData(sheetId);
    
    // Fallback for sosf if JSON loading fails
    if (!data && sheetId === 'sosf') {
        console.warn('SheetConfig: JSON loading failed, using fallback data for sosf');
        return createFallbackSosfData();
    }
    
    return data;
}

// Fallback data for sosf sheet - minimal version for emergency use
function createFallbackSosfData() {
    const imageNames = [
        'civic-plaza.jpg', 'cathedral-nave.jpg', 'glass-atrium.jpg', 'cantilever-bridge.jpg', 'modular-facade.jpg', 'concrete-pavilion.jpg',
        'spiral-stair.jpg', 'vaulted-arcade.jpg', 'urban-courtyard.jpg', 'winter-garden.jpg', 'observation-deck.jpg', 'brise-soleil.jpg',
        'ferry-terminal.jpg', 'service-corridor.jpg', 'waterfront-promenade.jpg', 'limestone-colonnade.jpg', 'temple-portico.jpg', 'covered-walkway.jpg',
        'reflecting-pool.jpg', 'sky-lobby.jpg', 'cloister-garden.jpg', 'lightwell-void.jpg', 'oceanfront-esplanade.jpg', 'quiet-courtyard.jpg',
        'campus-quad.jpg', 'canal-footbridge.jpg', 'riverwalk-terrace.jpg', 'stone-buttress.jpg', 'oculus-skylight.jpg', 'masonry-arch.jpg',
        'storm-canopy.jpg', 'twilight-facade.jpg', 'valley-viaduct.jpg', 'cascade-stair.jpg', 'windward-terrace.jpg', 'zen-courtyard.jpg'
    ];

    return {
        id: 'sosf',
        title: 'The Shapes of San Francisco',
        description: 'A photographic journey through San Francisco',
        metadata: {
            sheetWidth: 5685,
            sheetHeight: 6100,
            imageWidth: 600,
            imageHeight: 900,
            horizontalMargin: 315,
            verticalMargin: 40,
            firstImageX: 250,
            firstImageY: 250,
            rows: 6,
            columns: 6,
            scale: 0.0006161398
        },
        images: imageNames.map((filename, index) => ({
            id: filename.replace('.jpg', ''),
            filename: filename,
            title: filename.replace('.jpg', '').replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()),
            description: `Beautiful photograph: ${filename.replace('.jpg', '').replace('-', ' ')}`,
            altText: filename.replace('.jpg', '').replace('-', ' '),
            tags: ['photography'],
            position: { row: Math.floor(index / 6), col: index % 6 },
            products: [
                { type: 'fine_art_print', name: 'Fine Art Print', sku: 'GLOBAL-CFPM-16X20', basePrice: 89.99 },
                { type: 'poster', name: 'Poster Print', sku: 'GLOBAL-POST-16X20', basePrice: 24.99 },
                { type: 'canvas', name: 'Canvas Print', sku: 'GLOBAL-CAN-16X20', basePrice: 119.99 }
            ]
        }))
    };
}

export async function getAvailableSheets() {
    return await dataLoader.getAvailableSheets();
}

// Input validation
export async function validateSheetId(sheetId) {
    if (!sheetId || typeof sheetId !== 'string') return false;
    if (!/^[a-zA-Z0-9_-]+$/.test(sheetId)) return false;
    
    const availableSheets = await getAvailableSheets();
    return availableSheets.includes(sheetId);
}

export function sanitizeFilename(filename) {
    if (!filename || typeof filename !== 'string') return '';
    return filename.replace(/[^a-zA-Z0-9.\-_áéíóúàèìòùâêîôûäëïöüčřšžýň ]/g, '');
} 